import React, { useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { 
  Bars3Icon, 
  XMarkIcon,
  CubeIcon,
  TagIcon,
  ChartBarIcon,
  CpuChipIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  TruckIcon,
  BuildingOfficeIcon,
  LinkIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CogIcon,
  GlobeAltIcon,
  BugAntIcon,
  SparklesIcon,
  ChatBubbleLeftIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import ProductManagementPanel from '../components/ProductManagementPanel';
import OrderManagementPanel from '../components/OrderManagementPanel';
import UserManagementPanel from '../components/UserManagementPanel';
import ReturnManagementPanel from '../components/ReturnManagementPanel';
import ReturnSettingsPanel from '../components/ReturnSettingsPanel';
import ReturnTrackingManagementPanel from '../components/ReturnTrackingManagementPanel';
import SettingsManagementPanel from '../components/SettingsManagementPanel';
import KargonomiSettings from '../components/KargonomiSettings';
import ShippingManagementPanel from '../components/ShippingManagementPanel';
import CategoryManagementPanel from '../components/CategoryManagementPanel';
import CategoryAnalyticsPanel from '../components/CategoryAnalyticsPanel';
import AdvancedCategoryAnalyticsPanel from '../components/AdvancedCategoryAnalyticsPanel';
import TestDataPanel from '../components/TestDataPanel';
import AIRecommendationsPanel from '../components/AIRecommendationsPanel';
import AutoTrackingPanel from '../components/AutoTrackingPanel';
import ShippingCompaniesPanel from '../components/ShippingCompaniesPanel';
import KargonomiDebugPanel from '../components/KargonomiDebugPanel';
import CollectionManagementPanel from '../components/CollectionManagementPanel';
import CampaignManagementPanel from '../components/CampaignManagementPanel';
import AdminCampaignTrackingPanel from '../components/AdminCampaignTrackingPanel';
import CampaignReportingDashboard from '../components/CampaignReportingDashboard';
import ReviewManagementPanel from '../components/ReviewManagementPanel';
import PaymentMethodsPanel from '../components/PaymentMethodsPanel';
import AdminPaymentTransactions from './AdminPaymentTransactions';
import AdminPaymentLogs from './AdminPaymentLogs';
import AdminPaymentTestTools from './AdminPaymentTestTools';
import AdminPaymentTestCards from './AdminPaymentTestCards';
import AdminPaymentSettings from './AdminPaymentSettings';
import { useNavigate } from 'react-router-dom';
// Sürükle-bırak için item tipi
const ItemTypes = {
  MENU_ITEM: 'menuItem'
};

// Sürüklenebilir menü öğesi bileşeni
function DraggableMenuItem({ item, index, moveItem, isActive, onClick, isSidebarCollapsed }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.MENU_ITEM,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.MENU_ITEM,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const IconComponent = item.icon;

  return (
    <div
      ref={(node) => drag(drop(node))}
      onClick={onClick}
      className={`
        group flex items-center px-3 py-3 text-sm font-medium rounded-xl cursor-pointer transition-all duration-300 relative
        ${isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}
        ${isActive 
          ? 'bg-gradient-to-r from-brand-green to-brand-green-light text-white shadow-lg transform scale-105' 
          : 'text-gray-700 hover:bg-gray-100 hover:text-brand-green hover:shadow-md hover:scale-105'
        }
        ${isSidebarCollapsed ? 'justify-center' : ''}
      `}
      style={{ 
        cursor: isDragging ? 'grabbing' : 'grab',
        transform: isDragging ? 'rotate(5deg)' : 'rotate(0deg)'
      }}
    >
      <IconComponent className={`h-5 w-5 ${isSidebarCollapsed ? '' : 'mr-3'} transition-colors duration-300`} />
      {!isSidebarCollapsed && (
        <span className="truncate">{item.name}</span>
      )}
      {isDragging && (
        <div className="absolute inset-0 bg-brand-green bg-opacity-20 rounded-xl animate-pulse"></div>
      )}
      
      {/* Sürükleme göstergesi */}
      <div className={`absolute right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${isSidebarCollapsed ? 'hidden' : ''}`}>
        <div className="flex flex-col space-y-0.5">
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ openModal, closeModal }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMenuLoading, setIsMenuLoading] = useState(true);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Varsayılan menü öğeleri
  const defaultMenuItems = [
    { 
      id: 'products', 
      name: 'Ürünler', 
      icon: CubeIcon, 
      component: <ProductManagementPanel openModal={openModal} closeModal={closeModal} /> 
    },
    { 
      id: 'categories', 
      name: 'Kategoriler', 
      icon: TagIcon, 
      component: <CategoryManagementPanel openModal={openModal} closeModal={closeModal} /> 
    },
    { 
      id: 'collections', 
      name: 'Koleksiyon Yönetimi', 
      icon: SparklesIcon, 
      component: <CollectionManagementPanel /> 
    },
    { 
      id: 'campaigns', 
      name: 'Kampanya Yönetimi', 
      icon: TagIcon, 
      component: <CampaignManagementPanel /> 
    },
    { 
      id: 'campaign-tracking', 
      name: 'Kampanya Takip', 
      icon: ChartBarIcon, 
      component: <AdminCampaignTrackingPanel /> 
    },
    { 
      id: 'campaign-reports', 
      name: 'Kampanya Raporları', 
      icon: ChartBarIcon, 
      component: <CampaignReportingDashboard /> 
    },
    { 
      id: 'analytics', 
      name: 'Kategori Analytics', 
      icon: ChartBarIcon, 
      component: <AdvancedCategoryAnalyticsPanel /> 
    },
    { 
      id: 'ai', 
      name: 'AI Önerileri', 
      icon: CpuChipIcon, 
      component: <AIRecommendationsPanel /> 
    },
    { 
      id: 'orders', 
      name: 'Siparişler', 
      icon: ClipboardDocumentListIcon, 
      component: <OrderManagementPanel openModal={openModal} closeModal={closeModal} /> 
    },
    { 
      id: 'payment-methods', 
      name: 'Ödeme Yöntemleri', 
      icon: CreditCardIcon, 
      component: <PaymentMethodsPanel /> 
    },
    { 
      id: 'payment-transactions', 
      name: 'Ödeme İşlemleri', 
      icon: ChartBarIcon, 
      component: <AdminPaymentTransactions />
    },
    { 
      id: 'payment-logs', 
      name: 'Ödeme Logları', 
      icon: ClipboardDocumentListIcon, 
      component: <AdminPaymentLogs />
    },
    { 
      id: 'payment-test-tools', 
      name: 'Test Araçları', 
      icon: CogIcon, 
      component: <AdminPaymentTestTools />
    },
    { 
      id: 'payment-test-cards', 
      name: 'Test Kartları', 
      icon: CreditCardIcon, 
      component: <AdminPaymentTestCards />
    },
    { 
      id: 'payment-settings', 
      name: 'Ödeme Ayarları', 
      icon: CogIcon, 
      component: <AdminPaymentSettings />
    },
    { 
      id: 'users', 
      name: 'Kullanıcılar', 
      icon: UsersIcon, 
      component: <UserManagementPanel openModal={openModal} closeModal={closeModal} /> 
    },
    { 
      id: 'reviews', 
      name: 'Yorum Yönetimi', 
      icon: ChatBubbleLeftIcon, 
      component: <ReviewManagementPanel /> 
    },
    { 
      id: 'shipping', 
      name: 'Kargo', 
      icon: TruckIcon, 
      component: <ShippingManagementPanel openModal={openModal} closeModal={closeModal} /> 
    },
    { 
      id: 'companies', 
      name: 'Kargo Şirketleri', 
      icon: BuildingOfficeIcon, 
      component: <ShippingCompaniesPanel /> 
    },
    { 
      id: 'kargonomi', 
      name: 'Kargonomi API', 
      icon: LinkIcon, 
      component: <KargonomiSettings /> 
    },
    { 
      id: 'tracking', 
      name: 'Otomatik Takip', 
      icon: ArrowPathIcon, 
      component: <AutoTrackingPanel />
    },
    { 
      id: 'debug-kargonomi', 
      name: 'Kargonomi Debug', 
      icon: BugAntIcon, 
      component: <KargonomiDebugPanel /> 
    },
    { 
      id: 'returns', 
      name: 'İadeler', 
      icon: ArrowUturnLeftIcon, 
      component: <ReturnManagementPanel /> 
    },
    { 
      id: 'return-settings', 
      name: 'İade Ayarları', 
      icon: CogIcon, 
      component: <ReturnSettingsPanel /> 
    },
    { 
      id: 'return-tracking', 
      name: 'İade Takip Yönetimi', 
      icon: ArrowPathIcon, 
      component: <ReturnTrackingManagementPanel /> 
    },
    { 
      id: 'settings', 
      name: 'Site Ayarları', 
      icon: GlobeAltIcon, 
      component: <SettingsManagementPanel /> 
    },
  ];

  // Menü öğelerini state olarak tanımlıyoruz ki sürükle-bırak ile sıralayabilelim
  const [menuItems, setMenuItems] = useState(defaultMenuItems);

  // Menü sıralamasını yükle
  const loadMenuOrder = useCallback(async () => {
    try {
      setIsMenuLoading(true);
      console.log('Menü sıralaması yükleniyor...');
      
      const response = await api.get('/api/admin-menu/get-menu-order');
      
      if (response.success && response.data) {
        console.log('Kaydedilmiş menü sıralaması bulundu:', response.data);
        
        // Kaydedilmiş sıralamaya göre menü öğelerini yeniden sırala
        const orderedItems = [];
        const remainingItems = [...defaultMenuItems];
        
        // Önce kaydedilmiş sıralamadaki öğeleri ekle
        response.data.forEach(itemId => {
          const item = remainingItems.find(item => item.id === itemId);
          if (item) {
            orderedItems.push(item);
            const index = remainingItems.indexOf(item);
            remainingItems.splice(index, 1);
          }
        });
        
        // Sonra kalan öğeleri ekle (yeni eklenen menü öğeleri için)
        orderedItems.push(...remainingItems);
        
        setMenuItems(orderedItems);
      } else {
        console.log('Kaydedilmiş menü sıralaması bulunamadı, varsayılan sıralama kullanılıyor');
        setMenuItems(defaultMenuItems);
      }
    } catch (error) {
      console.error('Menü sıralaması yükleme hatası:', error);
      setMenuItems(defaultMenuItems);
    } finally {
      setIsMenuLoading(false);
    }
  }, []);

  // Menü sıralamasını kaydet
  const saveMenuOrder = useCallback(async (items) => {
    try {
      setIsSavingOrder(true);
      console.log('Menü sıralaması kaydediliyor...');
      
      const menuOrder = items.map(item => item.id);
      
      const response = await api.post('/api/admin-menu/save-menu-order', {
        menuOrder
      });
      
      if (response.success) {
        console.log('Menü sıralaması başarıyla kaydedildi');
      } else {
        console.error('Menü sıralaması kaydetme hatası:', response.error);
      }
    } catch (error) {
      console.error('Menü sıralaması kaydetme hatası:', error);
    } finally {
      setIsSavingOrder(false);
    }
  }, []);

  // Menü öğelerini sürükle-bırak ile yeniden sıralama
  const moveItem = useCallback((dragIndex, hoverIndex) => {
    setMenuItems((prevItems) => {
      const newItems = [...prevItems];
      const draggedItem = newItems[dragIndex];
      newItems.splice(dragIndex, 1);
      newItems.splice(hoverIndex, 0, draggedItem);
      
      // Yeni sıralamayı kaydet (debounce ile)
      setTimeout(() => {
        saveMenuOrder(newItems);
      }, 1000);
      
      return newItems;
    });
  }, [saveMenuOrder]);

  // Component mount olduğunda menü sıralamasını yükle
  useEffect(() => {
    loadMenuOrder();
  }, [loadMenuOrder]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
        {/* Mobil Overlay */}
        {isMobileSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
          ${isSidebarCollapsed ? 'w-16' : 'w-72'}
          ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          transition-all duration-300 ease-in-out
          bg-white shadow-xl border-r border-gray-200
        `}>
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            {!isSidebarCollapsed && (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center">
                  <CogIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <h2 className="text-lg font-bold text-text-dark">Yönetim</h2>
                  {isSavingOrder && (
                    <div className="flex items-center space-x-1 text-xs text-blue-600">
                      <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Menü kaydediliyor...</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                title={isSidebarCollapsed ? 'Genişlet' : 'Daralt'}
              >
                <Bars3Icon className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Sidebar Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {isMenuLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-6 h-6 border-2 border-brand-green border-t-transparent rounded-full animate-spin"></div>
                  {!isSidebarCollapsed && (
                    <span className="text-xs text-gray-500">Menü yükleniyor...</span>
                  )}
                </div>
              </div>
            ) : (
              menuItems.map((item, index) => (
                <DraggableMenuItem
                  key={item.id}
                  item={item}
                  index={index}
                  moveItem={moveItem}
                  isActive={activeTab === index}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      setActiveTab(index);
                    }
                    setIsMobileSidebarOpen(false);
                  }}
                  isSidebarCollapsed={isSidebarCollapsed}
                />
              ))
            )}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className={`text-xs text-gray-500 ${isSidebarCollapsed ? 'text-center' : ''}`}>
              {!isSidebarCollapsed && (
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  </div>
                  <span>Sürükleyerek sıralayın</span>
                </div>
              )}
              {isSidebarCollapsed && '⚡'}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <Bars3Icon className="h-6 w-6 text-gray-600" />
              </button>
              <h1 className="text-lg font-bold text-text-dark">
                {menuItems[activeTab]?.name}
              </h1>
              <div className="w-10"></div>
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden lg:block bg-white shadow-sm border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-dark mb-2">
                  {menuItems[activeTab]?.name}
                </h1>
                <p className="text-text-light">
                  Tüm sistem yönetimi işlemlerinizi buradan gerçekleştirebilirsiniz
                </p>
              </div>
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-brand-green to-brand-green-light rounded-2xl shadow-lg">
                {React.createElement(menuItems[activeTab]?.icon, { 
                  className: "w-8 h-8 text-white" 
                })}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 p-4 lg:p-6 overflow-y-auto">
            <div className="animate-fade-in">
              {menuItems[activeTab]?.component}
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}

export default AdminPanel;
